'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ChatMessage, TaskFile } from '@/entities/interview-task';
import { useAiDialog } from '@/features/ai-dialog';
import { isSkipCommand } from '@/features/candidate-interview';
import {
	type AIFeedbackTarget,
	captureAIFeedbackTarget,
} from '@/features/feedback/lib/ai-feedback';
import { useToast } from '@/features/toast';
import { useConfirm } from '@/shared';
import {
	AIAttachmentFileFormatV1,
	type AIAttachmentV1,
	AIDialogStatusV1,
	AIProblemTaskTypeV1,
	AIProblemTypeV1,
	type AIVisibleMessageV1,
	type GetAIDialogResponseV1,
	type GetAIProblemResponseV1,
} from '@/shared/api';
import { useIsMobile } from '@/shared/hooks/use-mobile';

import { isExitCommand } from '../lib/commands';
import { useControlsAnalytics } from '../model/use-controls-analytics';
import { useDialogSolution } from '../model/use-dialog-solution';
import { useEditorDrafts } from '../model/use-editor-drafts';
import type { BreadcrumbItem } from './breadcrumbs';
import { ChatSurface } from './chat-layout';
import { ChatPane, type ChatPaneHandle } from './chat-pane';
import { DialogSolutionPanel } from './dialog-solution-panel';
import type { DialogNavigation } from './dialog-topbar';
import { FeedbackDialog } from './feedback-dialog';
import { OpenedQuiz, SidePanel } from './side-panel';
import type { SidePanelFooterProps } from './side-panel-footer';
import { SubmitConfirmDialog } from './submit-confirm-dialog';

type InterviewSessionProps = {
	navigation?: DialogNavigation;
	candidate?: {
		canWrite: boolean;
		secondsLeft: number;
		progress: { current: number; total: number };
		autoNextSeconds?: number;
		totalTime: string;
		closed: boolean;
		timedOut: boolean;
		busy: boolean;
		onSkip: () => Promise<void | boolean>;
		onNext: () => Promise<void | boolean>;
		onFinish: () => Promise<void | boolean>;
	};
	problemId: string;
	problem: GetAIProblemResponseV1;
	dialogId: string;
	initialDialog: GetAIDialogResponseV1;
	initialMessages: AIVisibleMessageV1[];
	/** Создаёт новый диалог и пересоздаёт сессию (кнопка «Пройти заново»). */
	onRestart: () => void | boolean | Promise<void | boolean>;
	/** Куда ведёт кнопка «Далее» / «К задачам» по завершении. */
	nextHref: string;
	nextLabel: string;
	/** Соседние задачи курса для стрелок в шапке (undefined на границах курса). */
	coursePrevHref?: string;
	courseNextHref?: string;
	breadcrumbs: BreadcrumbItem[];
};

// Границы ширины чата при ресайзе сплита (в % от контейнера).
const MIN_CHAT_PCT = 35;
const MAX_CHAT_PCT = 75;
const DEFAULT_CHAT_PCT = 56;
// Длительность open/close-анимации панели; должна совпадать с duration-[…] во flex.
const PANEL_ANIM_MS = 320;

// useIsMobile детектит вьюпорт в effect'е → на первом рендере (в т.ч. сразу
// после перехода вперёд/назад) возвращает false. Авто-эффекты должны знать
// размер уже на маунте, иначе файл успевает авто-открыться до детекта. Читаем
// медиазапрос синхронно (брейкпоинт совпадает с use-mobile: <768px).
const isMobileViewport = () =>
	typeof window !== 'undefined' &&
	window.matchMedia('(max-width: 767px)').matches;

const TASK_LANG_TO_FILE_FORMAT: Partial<
	Record<string, AIAttachmentFileFormatV1>
> = {
	cpp: AIAttachmentFileFormatV1.Cpp,
	py: AIAttachmentFileFormatV1.Py,
	java: AIAttachmentFileFormatV1.Java,
	go: AIAttachmentFileFormatV1.Go,
	cs: AIAttachmentFileFormatV1.Cs,
	js: AIAttachmentFileFormatV1.Js,
	sql: AIAttachmentFileFormatV1.Sql,
};

export function InterviewSession({
	navigation,
	candidate,
	problemId,
	problem,
	dialogId,
	initialDialog,
	initialMessages,
	onRestart,
	nextHref,
	nextLabel,
	coursePrevHref,
	courseNextHref,
	breadcrumbs,
}: InterviewSessionProps) {
	const router = useRouter();
	const confirm = useConfirm();
	const { addToast } = useToast();
	const isMobile = useIsMobile();

	const isInterviewProblem =
		!!candidate || problem.type === AIProblemTypeV1.AiInterview;

	const track = useControlsAnalytics(
		candidate ? 'candidate' : isInterviewProblem ? 'interview' : 'practice',
		problemId,
		dialogId,
		candidate
			? 'candidate'
			: navigation?.backHref.startsWith('/course/')
				? 'course'
				: navigation?.backHref.startsWith('/roadmap/')
					? 'roadmap'
					: 'standalone',
	);
	const dialog = useAiDialog({ dialogId, initialDialog, initialMessages });
	const {
		dialog: dialogData,
		currentTask,
		messages,
		isThinking,
		canSend,
		sendMessage,
		completeStaticTask,
		completeSelectTask,
		submitCode,
		stopDialog,
		reportDialog,
	} = dialog;

	const drafts = useEditorDrafts();

	const [openedFile, setOpenedFile] = useState<TaskFile | null>(null);
	const [openedQuiz, setOpenedQuiz] = useState<OpenedQuiz | null>(null);
	const [draft, setDraft] = useState('');
	const [openedSolution, setOpenedSolution] = useState(false);
	const solutionOpener = useRef<HTMLElement | null>(null);
	const restoreSolutionFocus = useRef(false);
	useEffect(() => {
		if (!openedSolution && restoreSolutionFocus.current) {
			restoreSolutionFocus.current = false;
			solutionOpener.current?.focus({ preventScroll: true });
		}
	}, [openedSolution]);
	const solutionFile = messages
		.flatMap((message) => message.attaches ?? [])
		.find((file) => 'lang' in file);
	const solutionLanguage =
		solutionFile && 'lang' in solutionFile
			? (
					{ py: 1, cpp: 2, go: 3, java: 4, cs: 5, js: 6 } as Record<
						string,
						number
					>
				)[solutionFile.lang]
			: undefined;
	const solution = useDialogSolution(
		problemId,
		!candidate && problem.hasStaticView ? solutionLanguage : undefined,
		track,
	);
	const openSolution = () => {
		solutionOpener.current = document.activeElement as HTMLElement;
		setOpenedSolution(true);
		track('solution', { action: 'open', stage: 'intent' });
	};
	const closeSolution = () => {
		restoreSolutionFocus.current = true;
		setOpenedSolution(false);
		track('solution', { action: 'close' });
	};
	const [submitConfirmFiles, setSubmitConfirmFiles] = useState<
		TaskFile[] | null
	>(null);
	const [feedback, setFeedback] = useState<AIFeedbackTarget | null>(null);

	// Анимация закрытия панели: на время transition держим контент в DOM
	// (openedFile/openedQuiz остаются), а flex чата гоним обратно к 100% —
	// панель «выезжает» обратно. По завершении таймера контент размонтируется.
	const [isClosing, setIsClosing] = useState(false);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// messageId последнего сообщения с файлами, которое уже авто-открывали —
	// чтобы не открывать его повторно на каждом ререндере.
	const autoOpenedFileMsgRef = useRef<number | null>(null);

	const cancelClose = useCallback(() => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
		setIsClosing(false);
	}, []);

	useEffect(() => () => cancelClose(), [cancelClose]);

	// Диалог всегда уже создан на сервере → pre-start gate не нужен.
	const isInterviewStarted = true;

	// При смене диалога (например, «Пройти заново») сбрасываем локальный UI:
	// закрываем панель, очищаем черновики и поле ввода. Сообщения чистит store.
	useEffect(() => {
		cancelClose();
		setOpenedFile(null);
		setOpenedQuiz(null);
		setDraft('');
		setOpenedSolution(false);
		setSubmitConfirmFiles(null);
		setFeedback(null);
		drafts.clearAllDrafts();
		autoOpenedFileMsgRef.current = null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialogId]);

	// Таймер
	const deadlineMs = useMemo(() => {
		const raw = dialogData?.deadlineAt;
		if (!raw) return 0;
		// ВОРКЭРАУНД: бэкенд отдаёт deadlineAt как московское время (UTC+3),
		// но помечает его суффиксом «Z» (UTC). Из-за этого new Date(...) читает
		// его как UTC и сдвигает дедлайн на 3 часа вперёд — таймер «замирает»
		// на дефолте. Трактуем «Z» как реальный офсет +03:00. Если строка уже
		// несёт явный офсет (без «Z»), replace ничего не меняет.
		// TODO: убрать, когда бэкенд начнёт присылать корректный часовой пояс.
		return new Date(raw.replace('Z', '+03:00')).getTime();
	}, [dialogData?.deadlineAt]);

	// Интервью завершено — финал диалога, досрочное завершение или фаза генерации
	// отчёта; в этих статусах обратный отсчёт замораживаем и показываем дефолт.
	const isInterviewOver =
		dialogData?.status === AIDialogStatusV1.Finished ||
		dialogData?.status === AIDialogStatusV1.Aborted ||
		dialogData?.status === AIDialogStatusV1.AiReport;

	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!isInterviewProblem || !deadlineMs || isInterviewOver) return;
		const id = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(id);
	}, [isInterviewProblem, deadlineMs, isInterviewOver]);

	// Если бэкенд задаёт дедлайн с буфером, сырой остаток на старте может быть
	// больше дефолта (например, 30:05 при лимите 30:00). Зажимаем сверху, чтобы
	// таймер не «прыгал» выше положенного.
	const defaultSeconds = problem.timeLimitMinutes * 60;
	const secondsLeft = candidate
		? candidate.secondsLeft
		: !deadlineMs || isInterviewOver
			? defaultSeconds
			: Math.max(
					0,
					Math.min(defaultSeconds, Math.floor((deadlineMs - now) / 1000)),
				);
	const timerMm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
	const timerSs = String(secondsLeft % 60).padStart(2, '0');
	const timerLow = secondsLeft < 300;

	// Время вышло: дедлайн наступил, а интервью ещё не в терминальном статусе.
	const isTimeUp =
		isInterviewProblem && !!deadlineMs && !isInterviewOver && secondsLeft === 0;

	// По окончании таймера завершаем интервью запросом на генерацию отчёта.
	// Стреляем один раз на диалог; ref сбрасывается при смене dialogId.
	const reportFiredRef = useRef(false);
	useEffect(() => {
		reportFiredRef.current = false;
	}, [dialogId]);

	useEffect(() => {
		if (candidate || !isTimeUp || reportFiredRef.current) return;
		reportFiredRef.current = true;
		void (async () => {
			const result = await reportDialog();
			if (!result.ok) {
				addToast(
					'Ошибка',
					result.error ?? 'Не удалось завершить интервью по таймеру.',
					'error',
				);
			}
		})();
	}, [candidate, isTimeUp, reportDialog, addToast]);

	// Pinned bar — индекс последнего assistant сообщения с файлами.
	const latestFileMsgIdx = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (
				messages[i].role === 'interviewer' &&
				(messages[i].attaches || []).length > 0
			) {
				return i;
			}
		}
		return -1;
	}, [messages]);

	const pinnedFiles = useMemo(
		() =>
			latestFileMsgIdx >= 0 ? (messages[latestFileMsgIdx].attaches ?? []) : [],
		[messages, latestFileMsgIdx],
	);

	// Все файлы диалога по порядку (вложения всех сообщений интервьюера) —
	// для линейной навигации «Далее» по завершённому интервью и определения
	// последнего файла всего диалога.
	const allFiles = useMemo(() => {
		const files: TaskFile[] = [];
		for (const m of messages) {
			if (m.role === 'interviewer' && m.attaches?.length) {
				files.push(...m.attaches);
			}
		}
		return files;
	}, [messages]);

	const latestQuizMsgIdx = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'interviewer' && messages[i].quiz) return i;
		}
		return -1;
	}, [messages]);

	// Авто-открытие правой панели по currentTask.taskType.
	// На мобильной версии панель открывается только вручную (по тапу на файл).
	useEffect(() => {
		if (isMobileViewport()) return;
		const taskType = currentTask?.taskType;
		setOpenedSolution(false);
		if (!taskType) return;
		cancelClose();
		if (taskType === AIProblemTaskTypeV1.Static) {
			// Последнее сообщение с attachments → берём первый theory/markdown файл.
			const candidate =
				pinnedFiles.find((f) => f.kind === 'theory' || f.kind === 'markdown') ??
				pinnedFiles[0];
			if (candidate) {
				setOpenedQuiz(null);
				setOpenedFile(candidate);
			}
		} else if (
			taskType === AIProblemTaskTypeV1.Select ||
			taskType === AIProblemTaskTypeV1.MultiSelect
		) {
			const msg = latestQuizMsgIdx >= 0 ? messages[latestQuizMsgIdx] : null;
			if (msg?.quiz) {
				setOpenedFile(null);
				setOpenedQuiz({
					question: msg.quiz.question,
					key: `${msg.messageId ?? latestQuizMsgIdx}`,
				});
			}
		} else if (
			taskType === AIProblemTaskTypeV1.Problem ||
			taskType === AIProblemTaskTypeV1.Code
		) {
			const editFile =
				pinnedFiles.find((f) => f.kind === 'edit') ?? pinnedFiles[0];
			if (editFile) {
				setOpenedQuiz(null);
				setOpenedFile(editFile);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentTask?.taskId, currentTask?.taskType]);

	// Авто-открытие свежего файла: когда приходит новое сообщение интервьюера с
	// файлами (например, ответ после отправки сообщения в чат) и правая панель
	// уже открыта — показываем новый файл. Закрытую панель не навязываем.
	useEffect(() => {
		if (isMobileViewport()) return;
		if (latestFileMsgIdx < 0) return;
		const msgId = messages[latestFileMsgIdx].messageId ?? latestFileMsgIdx;
		const prev = autoOpenedFileMsgRef.current;
		autoOpenedFileMsgRef.current = msgId;
		// Первый замер или то же сообщение — ничего не делаем.
		if (prev === null || prev === msgId) return;
		// Только если панель уже открыта (и не в процессе закрытия).
		if ((!openedFile && !openedQuiz) || isClosing) return;
		const file =
			pinnedFiles.find((f) => f.kind === 'edit') ??
			pinnedFiles.find((f) => f.kind === 'theory' || f.kind === 'markdown') ??
			pinnedFiles[0];
		if (!file) return;
		cancelClose();
		setOpenedQuiz(null);
		setOpenedFile(file);
	}, [
		latestFileMsgIdx,
		messages,
		pinnedFiles,
		openedFile,
		openedQuiz,
		isClosing,
		cancelClose,
	]);

	const handleLogout = useCallback(async () => {
		if (
			candidate?.busy ||
			(!candidate && dialogData?.status !== AIDialogStatusV1.Idle)
		)
			return false;
		if (candidate) {
			return await candidate.onFinish();
		}
		const ok = await confirm('', {
			title: 'Завершить интервью?',
			description: 'Интервью остановится, отчёта по нему не будет.',
			confirmText: 'Завершить',
		});
		if (!ok) {
			track('command', { action: 'exit', stage: 'cancel' });
			return false;
		}
		const result = await stopDialog();
		track('command', {
			action: 'exit',
			stage: result.ok ? 'success' : 'error',
		});
		if (!result.ok) {
			addToast(
				'Ошибка',
				result.error ?? 'Не удалось завершить интервью. Попробуйте ещё раз.',
				'error',
			);
		}
		return result.ok;
	}, [candidate, confirm, stopDialog, addToast, dialogData?.status, track]);

	const handleNext = useCallback(() => {
		if (candidate) {
			void candidate.onNext();
			return;
		}
		router.push(nextHref);
	}, [candidate, router, nextHref]);

	// Стрелки в шапке: переход к соседней задаче курса. Хэндлеры срабатывают
	// только при наличии ссылки (на границах курса стрелка неактивна).
	const handleCoursePrev = useCallback(() => {
		if (coursePrevHref) router.push(coursePrevHref);
	}, [router, coursePrevHref]);

	const handleCourseNext = useCallback(() => {
		if (courseNextHref) router.push(courseNextHref);
	}, [router, courseNextHref]);

	const handleResetDialog = useCallback(async () => {
		if (
			candidate ||
			isThinking ||
			![
				AIDialogStatusV1.Idle,
				AIDialogStatusV1.AiError,
				AIDialogStatusV1.Finished,
				AIDialogStatusV1.Aborted,
			].includes(dialogData?.status as AIDialogStatusV1)
		)
			return false;
		const ok = await confirm('', {
			title: 'Пройти заново?',
			description:
				'Будет создана новая беседа, текущий прогресс не сохранится.',
			confirmText: 'Пройти заново',
		});
		if (!ok) {
			track('command', { action: 'restart', stage: 'cancel' });
			return false;
		}
		try {
			const result = await onRestart();
			if (result === false) throw new Error('restart_failed');
			track('command', {
				action: 'restart',
				stage: 'success',
			});
			return result;
		} catch {
			track('command', { action: 'restart', stage: 'error' });
			addToast(
				'Ошибка',
				'Не удалось начать заново. Попробуйте ещё раз.',
				'error',
			);
			return false;
		}
	}, [
		candidate,
		confirm,
		onRestart,
		isThinking,
		dialogData?.status,
		track,
		addToast,
	]);

	const handleResetEdits = useCallback(async () => {
		if (!openedFile || openedFile.kind !== 'edit') return;
		const ok = await confirm('', {
			title: 'Подтвердите сброс кода',
			description: `Код в ${openedFile.name} будет сброшен к начальному состоянию без возможности восстановления.`,
			confirmText: 'Подтвердить сброс',
		});
		if (!ok) return;
		drafts.resetEditorDraft(openedFile);
	}, [confirm, drafts, openedFile]);

	// Открытие панели: пока чат анимирует flex-basis, тяжёлые виджеты внутри
	// панели (CodeEditor/Monaco, Markdown) откладываем — иначе их синхронный
	// маунт конкурирует с transition и анимация выглядит «рваной». В это время
	// в области контента показываем лоадер.
	const [isPanelOpening, setIsPanelOpening] = useState(false);
	const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wasPanelVisibleRef = useRef(false);

	useEffect(() => {
		const visible = !!openedFile || !!openedQuiz;
		if (visible && !wasPanelVisibleRef.current && !isClosing) {
			// Только что открыли с закрытого состояния.
			if (openTimerRef.current) clearTimeout(openTimerRef.current);
			setIsPanelOpening(true);
			openTimerRef.current = setTimeout(() => {
				setIsPanelOpening(false);
				openTimerRef.current = null;
			}, PANEL_ANIM_MS);
		}
		if (!visible) {
			if (openTimerRef.current) clearTimeout(openTimerRef.current);
			openTimerRef.current = null;
			setIsPanelOpening(false);
		}
		wasPanelVisibleRef.current = visible;
	}, [openedFile, openedQuiz, isClosing]);

	useEffect(
		() => () => {
			if (openTimerRef.current) clearTimeout(openTimerRef.current);
		},
		[],
	);

	// Анимированное закрытие: гоним flex чата к 100% (isClosing), контент
	// остаётся в DOM на время transition, затем размонтируем.
	const performClose = useCallback(() => {
		if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
		// На мобильной версии панель — оверлей на весь экран, анимации сплита нет:
		// закрываем сразу, без holdа контента в DOM.
		if (isMobile) {
			closeTimerRef.current = null;
			setOpenedFile(null);
			setOpenedQuiz(null);
			setIsClosing(false);
			return;
		}
		setIsClosing(true);
		closeTimerRef.current = setTimeout(() => {
			setOpenedFile(null);
			setOpenedQuiz(null);
			setIsClosing(false);
			closeTimerRef.current = null;
		}, PANEL_ANIM_MS);
	}, [isMobile]);

	const tryClosePanel = useCallback(async () => {
		if (openedQuiz) {
			performClose();
			return;
		}
		if (drafts.isDirty(openedFile)) {
			const ok = await confirm('', {
				title: 'Есть несохранённые правки',
				description: 'Закрыть редактор без отправки?',
				confirmText: 'Закрыть',
			});
			if (!ok) return;
		}
		performClose();
	}, [confirm, drafts, openedFile, openedQuiz, performClose]);

	// Code-таска: проверка идёт обычным сообщением (а не submitCode) — текст
	// «/submit» + вложения с отредактированными файлами.
	const isCodeActive = currentTask?.taskType === AIProblemTaskTypeV1.Code;

	// Снимки редактируемых файлов code-таски для отправки сообщением. Прикрепляем
	// все редактируемые файлы при каждой отправке (даже без правок). content
	// замораживаем как view — карточка в чате покажет именно отправленный код.
	const buildSubmissionFiles = useCallback((): TaskFile[] => {
		return pinnedFiles
			.filter(
				(f): f is Extract<TaskFile, { kind: 'edit' }> => f.kind === 'edit',
			)
			.map((f) => {
				const code = drafts.getDraft(f);
				return {
					kind: 'view',
					name: f.name,
					lang: f.lang,
					code,
					baseCode: code,
					lines: code.split('\n').length,
					version: 1,
				};
			});
	}, [pinnedFiles, drafts]);

	const handleSend = useCallback(async () => {
		const v = draft.trim();
		if (!v) return;
		if (candidate && isSkipCommand(v)) {
			const result = await candidate.onSkip();
			if (result !== false)
				setDraft((current) => (current === draft ? '' : current));
			return;
		}
		if (candidate && isExitCommand(v)) {
			const result = await candidate.onFinish();
			if (result !== false)
				setDraft((current) => (current === draft ? '' : current));
			return;
		}
		if ((candidate && !candidate.canWrite) || !canSend) return;
		track('send', { stage: 'intent' });
		// Офлайн: вызов server action не дойдёт до Next-сервера и «зависнет»,
		// не отклонившись, — поэтому проверяем соединение заранее. Поле ввода
		// не трогаем (текст остаётся), сообщение в чат не добавляем.
		if (typeof navigator !== 'undefined' && !navigator.onLine) {
			track('send', { stage: 'error', reason: 'network' });
			addToast(
				'Ошибка',
				'Не удалось отправить сообщение, попробуйте ещё раз.',
				'error',
			);
			return;
		}
		// В code-таске к сообщению прикрепляем отредактированные файлы.
		const attachFiles = isCodeActive ? buildSubmissionFiles() : undefined;
		setDraft('');
		const { ok, error } = await sendMessage(v, attachFiles);
		track('send', {
			stage: ok ? 'success' : 'error',
			...(!ok ? { reason: 'request' as const } : {}),
		});
		if (!ok) {
			// Сообщение не доставилось — возвращаем текст в поле ввода,
			// не затирая то, что пользователь успел набрать заново.
			setDraft((prev) => (prev ? prev : v));
			addToast(
				'Ошибка',
				error
					? `Не удалось отправить сообщение, попробуйте ещё раз. ${error}`
					: 'Не удалось отправить сообщение, попробуйте ещё раз.',
				'error',
			);
		}
	}, [
		candidate,
		track,
		canSend,
		draft,
		sendMessage,
		addToast,
		isCodeActive,
		buildSubmissionFiles,
	]);

	const handleConfirmTheoryRead = useCallback(async () => {
		if ((candidate && !candidate.canWrite) || !currentTask?.taskId) return;
		await completeStaticTask(currentTask.taskId);
	}, [candidate, completeStaticTask, currentTask?.taskId]);

	const handleQuizAttempt = useCallback((_answer: string) => {
		// Бэк сам пришлёт сообщение об ответе. Локально ничего не делаем.
	}, []);

	const handleQuizCorrect = useCallback(
		async (
			_answer: string,
			ctx: { optionIds: string[]; answerText: string },
		) => {
			if ((candidate && !candidate.canWrite) || !currentTask?.taskId) return;
			// TODO: бэк сейчас принимает один optionId. Для multi-select передаём первый —
			// если станет известна правильная схема (массив / разделители) — поправить.
			const optionId = ctx.optionIds[0];
			if (!optionId) return;
			await completeSelectTask(currentTask.taskId, optionId);
		},
		[candidate, completeSelectTask, currentTask?.taskId],
	);

	const handleQuizWrong = useCallback((_hint: string) => {
		// Подсказку выдаст бэк через свежее сообщение.
	}, []);

	const fileToAttachment = useCallback(
		(file: TaskFile): AIAttachmentV1 => {
			const code = file.kind === 'edit' ? drafts.getDraft(file) : '';
			const fileFormat =
				file.kind === 'edit' || file.kind === 'view'
					? (TASK_LANG_TO_FILE_FORMAT[file.lang] as
							| AIAttachmentFileFormatV1
							| undefined)
					: undefined;
			return {
				fileName: file.name,
				content: code,
				fileFormat,
				problemId,
			};
		},
		[drafts, problemId],
	);

	const doSubmit = useCallback(
		async (files: TaskFile[]) => {
			if (candidate && !candidate.canWrite) return;
			const payload = files.map(fileToAttachment);
			await submitCode(problemId, payload);
		},
		[candidate, fileToAttachment, problemId, submitCode],
	);

	const handleSubmitFix = useCallback(() => {
		if (!openedFile) return;
		const submittable = pinnedFiles.filter(
			(f) => f.kind === 'edit' || f.kind === 'view',
		);
		const filesToSubmit =
			submittable.length > 1 &&
			submittable.some((f) => f.name === openedFile.name)
				? submittable
				: [openedFile];
		if (filesToSubmit.length > 1) {
			setSubmitConfirmFiles(filesToSubmit);
			return;
		}
		doSubmit(filesToSubmit);
	}, [doSubmit, openedFile, pinnedFiles]);

	// Отправка решения code-таски: обычное сообщение «/submit» с вложенными
	// отредактированными файлами (бэк запускает программу по этому тексту).
	const handleSubmitCode = useCallback(async () => {
		if (candidate && !candidate.canWrite) return;
		const { ok, error } = await sendMessage('/submit', buildSubmissionFiles());
		if (!ok) {
			addToast(
				'Ошибка',
				error
					? `Не удалось отправить решение, попробуйте ещё раз. ${error}`
					: 'Не удалось отправить решение, попробуйте ещё раз.',
				'error',
			);
		}
	}, [candidate, sendMessage, buildSubmissionFiles, addToast]);

	// Status getters
	const getAttachStatus = useCallback(
		(m: ChatMessage, idx: number, file: TaskFile) => {
			if (m.role !== 'interviewer') return null;
			if (!(m.attaches || []).length) return null;
			if (idx !== latestFileMsgIdx) return 'accepted' as const;
			// Активное сообщение: pending если currentTask ещё активен.
			if (!currentTask) return 'accepted' as const;
			if (currentTask.taskType === AIProblemTaskTypeV1.Static) {
				return 'pending' as const;
			}
			if (
				currentTask.taskType === AIProblemTaskTypeV1.Problem ||
				currentTask.taskType === AIProblemTaskTypeV1.Code
			) {
				return file.kind === 'edit' ? ('pending' as const) : null;
			}
			return null;
		},
		[currentTask, latestFileMsgIdx],
	);

	const getQuizStatus = useCallback(
		(m: ChatMessage) => {
			if (!m.quiz) return null;
			const isLatest =
				latestQuizMsgIdx >= 0 && m === messages[latestQuizMsgIdx];
			if (!isLatest) return 'accepted' as const;
			const isActive =
				currentTask?.taskType === AIProblemTaskTypeV1.Select ||
				currentTask?.taskType === AIProblemTaskTypeV1.MultiSelect;
			return isActive ? ('pending' as const) : ('accepted' as const);
		},
		[currentTask?.taskType, latestQuizMsgIdx, messages],
	);

	const handleOpenFile = useCallback(
		(file: TaskFile) => {
			setOpenedSolution(false);
			cancelClose();
			setOpenedQuiz(null);
			setOpenedFile(file);
		},
		[cancelClose],
	);

	const chatPaneRef = useRef<ChatPaneHandle>(null);

	// Открыть файл и прокрутить/подсветить его сообщение в чате (как
	// scrollToPinned) — кнопка «Далее» при переходе к следующему файлу.
	const handleStepToFile = useCallback(
		(file: TaskFile) => {
			handleOpenFile(file);
			const msgIdx = messages.findIndex((m) => !!m.attaches?.includes(file));
			if (msgIdx >= 0) chatPaneRef.current?.scrollToMessage(msgIdx);
		},
		[handleOpenFile, messages],
	);

	const handleOpenQuiz = useCallback(
		(m: ChatMessage) => {
			if (!m.quiz) return;
			setOpenedSolution(false);
			cancelClose();
			setOpenedFile(null);
			setOpenedQuiz({
				question: m.quiz.question,
				key: `${m.messageId ?? Date.now()}`,
			});
		},
		[cancelClose],
	);

	// Открывает контент самого свежего шага: квиз либо файл из последнего
	// сообщения. Используется кнопкой «Далее» — переход к следующему шагу,
	// а не закрытие панели.
	const openLatestStepContent = useCallback(() => {
		cancelClose();
		const quizFresher =
			latestQuizMsgIdx >= 0 && latestQuizMsgIdx >= latestFileMsgIdx;
		if (quizFresher) {
			const quizMsg = messages[latestQuizMsgIdx];
			if (quizMsg?.quiz) {
				setOpenedFile(null);
				setOpenedQuiz({
					question: quizMsg.quiz.question,
					key: `${quizMsg.messageId ?? latestQuizMsgIdx}`,
				});
				return;
			}
		}
		if (pinnedFiles.length) {
			const file =
				pinnedFiles.find((f) => f.kind === 'edit') ??
				pinnedFiles.find((f) => f.kind === 'theory' || f.kind === 'markdown') ??
				pinnedFiles[0];
			setOpenedQuiz(null);
			setOpenedFile(file);
			return;
		}
		setOpenedFile(null);
		setOpenedQuiz(null);
	}, [cancelClose, messages, latestQuizMsgIdx, latestFileMsgIdx, pinnedFiles]);

	// Footer
	const isStaticActive = currentTask?.taskType === AIProblemTaskTypeV1.Static;
	const isProblemActive = currentTask?.taskType === AIProblemTaskTypeV1.Problem;
	const isQuizActive =
		currentTask?.taskType === AIProblemTaskTypeV1.Select ||
		currentTask?.taskType === AIProblemTaskTypeV1.MultiSelect;
	// Интервью решено — финал диалога (тот же статус, что показывает CompletedBanner).
	const isDialogFinished = dialogData?.status === AIDialogStatusV1.Finished;

	// «Далее» в футере теории: подтверждаем прочтение (если теория — часть
	// активной статической задачи) и открываем следующий файл из списка
	// вложений. Если файл последний и прочтение подтверждено — пришедший в
	// ответ файл откроет авто-эффект (панель открыта); иначе переходим к
	// свежему шагу (поведение прежнего «Далее»).
	const handleTheoryNext = useCallback(() => {
		// Сопоставляем по ссылке, а не по имени: имена файлов могут совпадать
		// в разных сообщениях (например, task.md на каждом шаге).
		const idx = openedFile ? pinnedFiles.indexOf(openedFile) : -1;
		const isActiveStaticTheory = isStaticActive && idx >= 0;

		if (isActiveStaticTheory) {
			void handleConfirmTheoryRead();
		}

		// На мобильной версии панель — оверлей: «Далее» подтверждает прочтение
		// и закрывает её, не подставляя следующий файл.
		if (isMobile) {
			performClose();
			return;
		}

		const next = idx >= 0 ? pinnedFiles[idx + 1] : undefined;
		if (next) {
			cancelClose();
			setOpenedQuiz(null);
			setOpenedFile(next);
			return;
		}

		if (!isActiveStaticTheory) {
			openLatestStepContent();
		}
	}, [
		openedFile,
		pinnedFiles,
		isStaticActive,
		cancelClose,
		handleConfirmTheoryRead,
		openLatestStepContent,
		isMobile,
		performClose,
	]);

	const footer: SidePanelFooterProps | null = (() => {
		if (openedQuiz) {
			if (isQuizActive) return null;
			// На мобильной «Далее» закрывает оверлей, а не открывает свежий шаг.
			return {
				type: 'quiz',
				onNext: isMobile ? performClose : openLatestStepContent,
			};
		}
		if (!openedFile) return null;
		// Завершённое интервью (показан CompletedBanner): только на последнем
		// файле ВСЕГО диалога — кнопка-дубль баннера (уход на nextHref); на
		// остальных файлах «Далее» открывает следующий файл диалога.
		// Сопоставляем по ссылке, а не по имени: имена файлов могут совпадать
		// в разных сообщениях (например, task.md в каждом шаге).
		if (isDialogFinished) {
			// Зелёная кнопка-дубль баннера — только на ПОСЛЕДНЕМ файле всего
			// диалога; на остальных решённых файлах — серая «Далее» к следующему.
			if (openedFile === allFiles[allFiles.length - 1]) {
				return {
					type: 'completed',
					onNext: handleNext,
					nextLabel,
					primary: true,
				};
			}
			const idx = allFiles.indexOf(openedFile);
			const nextFile = idx >= 0 ? allFiles[idx + 1] : undefined;
			return {
				type: 'completed',
				// На мобильной «Далее» закрывает оверлей, а не листает файлы внутри него.
				onNext: isMobile
					? performClose
					: nextFile
						? () => handleStepToFile(nextFile)
						: openLatestStepContent,
				nextLabel: 'Далее',
			};
		}
		if (openedFile.kind === 'theory' || openedFile.kind === 'markdown') {
			// По ссылке: одноимённый, но уже прочитанный файл из прошлого шага
			// не должен считаться активной теорией (иначе кнопка зелёная).
			const isPinnedTheory = pinnedFiles.includes(openedFile);
			const isRead = !(isStaticActive && isPinnedTheory);
			return {
				type: 'theory',
				isRead,
				onNext: handleTheoryNext,
			};
		}
		if (openedFile.kind === 'edit') {
			return {
				type: 'edit',
				onReset: handleResetEdits,
				// Code-таска отправляет код сообщением «/submit»; problem — submitCode.
				onSubmit: isCodeActive ? handleSubmitCode : handleSubmitFix,
			};
		}
		return null;
	})();

	// Действия в правой панели (в т.ч. «Отправить» решение) доступны только когда
	// бэк в idle и нет запроса в полёте. isThinking выставляется сразу при отправке
	// — до того как статус успеет смениться на ai-generating, — поэтому защищает от
	// повторной отправки в это окно.
	const actionsDisabled =
		dialogData?.status !== AIDialogStatusV1.Idle ||
		isThinking ||
		!!(candidate && !candidate.canWrite);

	// Feedback
	const openFeedback = useCallback(
		(messageId: number, sentiment: 'up' | 'down') => {
			setFeedback(
				captureAIFeedbackTarget(problemId, dialogId, messageId, sentiment),
			);
		},
		[problemId, dialogId],
	);
	const closeFeedback = () => setFeedback(null);
	const feedbackSaved = () => {
		addToast('Спасибо!', 'Ваш отзыв сохранён.', 'success');
		setFeedback(null);
	};

	const sidePanelVisible = openedSolution || !!openedFile || !!openedQuiz;
	const pinnedTitle = pinnedFiles[0]?.name ?? problem.title;

	// Ресайзимый сплит чат/панель. Ширина чата анимируется через flex-basis,
	// поэтому при открытии файла из закрытого состояния панель «выезжает»,
	// сдвигая чат. Во время drag-а транзишен отключаем (следуем за курсором).
	const mainRef = useRef<HTMLDivElement>(null);
	const [chatWidthPct, setChatWidthPct] = useState(DEFAULT_CHAT_PCT);
	const [isDragging, setIsDragging] = useState(false);
	// Анимацию flex-basis включаем только после первого кадра. Иначе при заходе
	// на задачу (в т.ч. переключении вперёд/назад) авто-открытие панели гонит
	// чат со 100% к рабочей ширине с transition — и кажется, что вся страница
	// «выезжает» справа налево. С отложенным включением панель появляется сразу
	// в нужной ширине, а ручное открытие/закрытие по-прежнему анимируется.
	const [splitAnimEnabled, setSplitAnimEnabled] = useState(false);
	useEffect(() => {
		const id = requestAnimationFrame(() => setSplitAnimEnabled(true));
		return () => cancelAnimationFrame(id);
	}, []);
	// Смещение точки клика внутри ресайзера от его левой границы (= правой
	// границы чата). Без него широкий ресайзер «прыгает» под курсор.
	const dragOffsetRef = useRef(0);

	useEffect(() => {
		if (!isDragging) return;
		const onMove = (e: MouseEvent) => {
			const el = mainRef.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			const pct =
				((e.clientX - dragOffsetRef.current - rect.left) / rect.width) * 100;
			setChatWidthPct(Math.min(MAX_CHAT_PCT, Math.max(MIN_CHAT_PCT, pct)));
		};
		const onUp = () => setIsDragging(false);
		document.addEventListener('mousemove', onMove);
		document.addEventListener('mouseup', onUp);
		document.body.style.cursor = 'col-resize';
		document.body.style.userSelect = 'none';
		return () => {
			document.removeEventListener('mousemove', onMove);
			document.removeEventListener('mouseup', onUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
	}, [isDragging]);

	const isDialogStopped = dialogData?.status === AIDialogStatusV1.Aborted;
	const isAiGenerating = dialogData?.status === AIDialogStatusV1.AiGenerating;
	// Завершить интервью можно только из idle — иначе бэк вернёт ai_interview_not_active.
	const canStopDialog = dialogData?.status === AIDialogStatusV1.Idle;

	const handleStartInterview = useCallback(() => {
		// Диалог уже создан на сервере — pre-start gate тут не задействуется.
	}, []);

	return (
		<ChatSurface>
			<div ref={mainRef} className="relative flex min-h-0 flex-1">
				{/* CHAT — ширина анимируется через flex-basis (push при открытии панели).
				    На мобильной версии чат всегда на всю ширину: панель — оверлей сверху. */}
				<div
					inert={isMobile && openedSolution}
					className={`flex min-w-0 flex-col ${isDragging || !splitAnimEnabled ? '' : 'transition-[flex-basis] duration-[320ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]'}`}
					style={{
						flex:
							!isMobile && sidePanelVisible && !isClosing
								? `0 0 ${chatWidthPct}%`
								: '1 1 100%',
					}}
				>
					<ChatPane
						track={track}
						solutionControl={
							!candidate &&
							problem.hasStaticView &&
							solutionLanguage !== undefined &&
							solution.result?.status !== 'unavailable'
								? {
										ready: solution.result?.status === 'ready',
										error: solution.result?.status === 'error',
										onOpen: openSolution,
									}
								: undefined
						}
						navigation={
							navigation ?? {
								backHref: candidate ? nextHref : '/problems',
								backLabel: candidate ? 'К собеседованию' : 'К задачам',
							}
						}
						title={problem.title}
						ref={chatPaneRef}
						isInterview={isInterviewProblem}
						messages={messages}
						initialMessages={initialMessages}
						isThinking={isThinking || isAiGenerating}
						latestFileMsgIdx={latestFileMsgIdx}
						pinnedTitle={candidate ? problem.title : pinnedTitle}
						pinnedBarShown={!isInterviewProblem || isInterviewStarted}
						breadcrumbs={breadcrumbs}
						hasCompletedBanner={isDialogFinished}
						hasStoppedBanner={isDialogStopped}
						candidateMode={!!candidate}
						candidateControls={candidate}
						canSendMessage={canSend && (!candidate || candidate.canWrite)}
						isCompaction={dialogData?.status === AIDialogStatusV1.AiCompaction}
						isReportPending={dialogData?.status === AIDialogStatusV1.AiReport}
						isInterviewStarted={isInterviewStarted}
						timerMm={timerMm}
						timerSs={timerSs}
						timerLow={timerLow}
						onOpenFile={handleOpenFile}
						onOpenQuiz={handleOpenQuiz}
						getAttachStatus={getAttachStatus}
						getQuizStatus={getQuizStatus}
						onRate={openFeedback}
						draft={draft}
						onDraftChange={setDraft}
						onSend={handleSend}
						onResetDialog={handleResetDialog}
						onNext={handleNext}
						nextLabel={nextLabel}
						coursePrevHref={coursePrevHref}
						courseNextHref={courseNextHref}
						onCoursePrev={handleCoursePrev}
						onCourseNext={handleCourseNext}
						onLogout={handleLogout}
						logoutDisabled={candidate ? candidate.busy : !canStopDialog}
						onStartInterview={handleStartInterview}
					/>
				</div>

				{/* RESIZER — только на десктопе (на мобильной панель занимает весь экран) */}
				{!isMobile && sidePanelVisible && (
					<div
						onMouseDown={(e) => {
							e.preventDefault();
							dragOffsetRef.current =
								e.clientX - e.currentTarget.getBoundingClientRect().left;
							setIsDragging(true);
						}}
						className="group flex w-3 shrink-0 cursor-col-resize flex-col items-center justify-center"
					>
						<div className="pointer-events-none h-10 w-1 rounded-full bg-gray-5 transition-colors group-hover:bg-gray-4/40" />
					</div>
				)}

				{/* SIDE PANEL — на десктопе flex-1 (заполняет освобождённое чатом место),
				    на мобильной — оверлей на весь экран поверх чата. */}
				{sidePanelVisible && (
					<div className={isMobile ? 'absolute inset-0 z-20 flex' : 'contents'}>
						<>
							{openedSolution ? (
								<DialogSolutionPanel
									result={solution.result}
									onClose={closeSolution}
									onRetry={solution.retry}
									track={track}
								/>
							) : (
								<SidePanel
									openedFile={openedFile}
									openedQuiz={openedQuiz}
									pinnedFiles={pinnedFiles}
									onSelectFile={handleOpenFile}
									onClose={tryClosePanel}
									getEditorDraft={drafts.getDraft}
									setEditorDraft={drafts.setEditorDraft}
									footer={footer}
									quizIsLocked={
										!isQuizActive || !!(candidate && !candidate.canWrite)
									}
									actionsDisabled={actionsDisabled}
									isOpening={isPanelOpening}
									onQuizAttempt={handleQuizAttempt}
									onQuizCorrect={handleQuizCorrect}
									onQuizWrong={handleQuizWrong}
								/>
							)}
						</>
					</div>
				)}
			</div>

			<SubmitConfirmDialog
				files={submitConfirmFiles}
				onClose={() => setSubmitConfirmFiles(null)}
				onConfirm={() => {
					if (submitConfirmFiles) {
						doSubmit(submitConfirmFiles);
						setSubmitConfirmFiles(null);
					}
				}}
			/>

			{feedback &&
				feedback.aiDialogId === dialogId.toUpperCase() &&
				feedback.aiProblemId === problemId && (
					<FeedbackDialog
						key={`${feedback.aiDialogId}:${feedback.aiMessageId}`}
						context={feedback}
						sentiment={feedback.sentiment}
						problemId={feedback.aiProblemId}
						problemTitle={problem.title}
						flow={
							candidate
								? 'candidate'
								: isInterviewProblem
									? 'interview'
									: 'practice'
						}
						onClose={closeFeedback}
						onSaved={feedbackSaved}
					/>
				)}
		</ChatSurface>
	);
}
